'use client';
import React, { useEffect, useState } from 'react';

import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { PageComponentImage } from '../../../../components/page-components/image/page-component-image';
import { PageComponentMain } from '../../../../components/page-components/main/page-component-main';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import { IMeetingplacePageHomeMinified } from '@/lib/fragments/meetingPlacePageHome-minified';
import OfferCard from '@/components/offercard/offer-card';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import useNewsletterPopupAutomation from '@/hooks/useNewsletterPopupAutomation';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import {
  useGetActiveOffers,
  useGetLibraryConstantsDictionaryData,
  useIsMPLivliFamily,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import { WithCountdown } from '@/components/cards/wrapper/WithCountdown';
import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import { getBrowserClient } from '@/lib/apolloClient';
import { useRelatedOffers } from '@/hooks/use-related-offers';
import { getListingPageSlugs } from '@/lib/queries/getEventAndOfferListPageSlug';
import { useUser } from '@/providers/UserProvider';

interface MeetingPlacePageOfferProps {
  metaData: any;
  node_locale: string;
  parentSlug: string;
  offerData: IMeetingPlacePageOfferItem | null;
  homeData: IMeetingplacePageHomeMinified | null;
  matchingOffersList: IMeetingPlacePageOfferItem[];
  parent?: any;
  showSpecialOfferRedemption: boolean;
  disclaimerText?: string | null;
  preview?: boolean;
}

export const MeetingPlacePageOffer = ({
  metaData,
  node_locale,
  parentSlug,
  offerData,
  homeData,
  matchingOffersList,
  parent,
  showSpecialOfferRedemption,
  disclaimerText,
  preview = false,
}: MeetingPlacePageOfferProps) => {
  const client = getBrowserClient(preview);
  const page = offerData;
  const homepage = homeData;
  const isLivliFamilyMP = useIsMPLivliFamily();
  const dictionary = useGetLibraryConstantsDictionaryData();
  const image = page?.coverImage ? page.coverImage : undefined;
  const pageName = page?.name ?? '';
  const isMobile = useIsMobile();
  const isWebView = useGetMobileAppWebViewHeader();
  const activeOfferIds = useGetActiveOffers();
  const config = useTenantConfig();
  const { user } = useUser();

  const isLivliFamily =
    config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true' &&
    page?.typeOfOffer?.internalName === 'specialOffer';

  const { relatedOffers, loading, error } = useRelatedOffers({
    currentOffer: page as IMeetingPlacePageOfferItem,
    activeOfferIds,
    node_locale,
  });

  const tenantName =
    page?.tenant && page.tenant.name
      ? page.tenant.name
      : page?.tenant && page.tenant?.brand
        ? page.tenant.brand.name
        : undefined;

  const logo =
    page?.tenant && page?.tenant.logo
      ? page.tenant.logo
      : page?.tenant?.brand && page.tenant.brand?.logo
        ? page.tenant.brand.logo
        : undefined;

  const matchingOffers: IMeetingPlacePageOfferItem[] = matchingOffersList;

  const [currOfferPage, setCurrOfferPage] = useState(1);
  const _offersListSize = 4;
  const slicedOfferList = matchingOffers?.slice(
    0,
    _offersListSize * currOfferPage,
  );
  const [offerListPageSlug, setOfferListPageSlug] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const fetchSlugs = async () => {
      const tag =
        config?.NEXT_PUBLIC_MEETINGPLACE_TAG_CONTENTFUL_ID ||
        config?.MEETINGPLACE_TAG_CONTENTFUL_ID ||
        '';
      const { offerListPageSlug } = await getListingPageSlugs(
        client,
        tag,
        node_locale,
        preview,
      );
      if (offerListPageSlug) setOfferListPageSlug(offerListPageSlug);
    };

    fetchSlugs();
  }, [node_locale]);

  // useEffect(() => {
  //   //  Filter offers for current tenant
  //   const ids = activeOfferIds?.map((offer) => offer?.sys?.id).filter(Boolean);

  //   if (!ids?.length) return;
  //   const fetchData = async () => {
  //     try {
  //       const result = await client.query({
  //         query: GET_ACTIVE_OFFERS,
  //         variables: {
  //           ids: ids,
  //           language: node_locale,
  //         },
  //         fetchPolicy: 'no-cache',
  //         errorPolicy: 'all',
  //       });
  //       const offers =
  //         (result?.data as any)?.meetingPlacePageOfferCollection?.items || [];
  //       setActiveOffers(offers);
  //     } catch (error) {
  //       console.error('Error fetching offers:', error);
  //     }
  //   };

  //   fetchData();
  // }, [activeOfferIds, node_locale]);

  const gtmParams: IGtmParameters = {
    event: 'view_offer_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: isLivliFamily
      ? 'For Livli Family'
      : page?.typeOfOffer?.internalName == 'ikeaFamilyMemberOffer'
        ? 'For Ikea Family'
        : 'Regular Offer',
    user_status: isLivliFamilyMP ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  useNewsletterPopupAutomation();

  return (
    <>
      {page && (
        <>
          {/* ✅ GTM PAGE VIEW */}
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ?? ''}
          />

          <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
            <Breadcrumbs
              breadcrumbs={[pageName]}
              homeText={homepage?.breadcrumbHomeText ?? ''}
              cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
              node_locale={node_locale}
              parent={parent}
            />
            <div className="grid grid-cols-11 gap-6 pt-5">
              <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
                <PageComponentImage image={image} node_locale={node_locale} />
              </div>
              <div className="relative col-span-12 lg:col-span-6">
                <WithCountdown key={page?.sys?.id} card={page}>
                  {({ countdownState }) => (
                    <PageComponentMain
                      header={pageName}
                      caption={page?.caption}
                      logo={logo}
                      typeTag={
                        isLivliFamily
                          ? dictionary?.livliFamily
                          : page?.typeOfOffer?.name
                      }
                      typeTagClass={
                        page?.typeOfOffer?.internalName === 'specialOffer'
                          ? 'text-text-sticker-green'
                          : ''
                      }
                      panelclasses={isMobile ? '!bg-background-primary' : ''}
                      validFrom={page?.validFrom}
                      validTo={page?.validTo}
                      richText={page?.body}
                      node_locale={node_locale}
                      showSocialShare={
                        homepage?.meetingPlace?.showSocialShare ?? false
                      }
                      tagItems={page?.offerCampaignTagsCollection?.items
                        ?.filter(Boolean)
                        ?.map((tag, index) => ({
                          id: `${page?.sys?.id || ''}-${index}`,
                          tagName: tag?.tagName || '',
                          colour: tag?.colour || '',
                        }))}
                      offerRedemptionDetails={page?.offerRedemptionDetails}
                      offerRedemptionMechanism={page?.offerRedemptionMechanism}
                      typeOfOffer={page?.typeOfOffer?.internalName}
                      contentful_id={page?.sys?.id}
                      tenantName={tenantName}
                      meetingPlaceHomePage={homepage?.meetingPlace}
                      showSpecialOfferRedemption={showSpecialOfferRedemption}
                      isLivliFamilyOffer={isLivliFamily}
                      beResourceId={page?.beResourceId}
                      offerTenantId={page?.tenant?.shopId}
                      favouriteId={page?.sys?.id}
                      favouriteType="offer"
                      countdownState={countdownState}
                      isLivliFamily={
                        config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true'
                      }
                      pathPrefix={config?.GATSBY_SITE_PATH_PREFIX ?? ''}
                      disclaimerText={disclaimerText}
                    />
                  )}
                </WithCountdown>
                <div className="-mx-6 mt-10 bg-brand-white sm:px-6 pt-8 lg:m-0 lg:p-0 lg:pt-0">
                  {relatedOffers && relatedOffers?.length > 0 && (
                    <>
                      <h2 className="px-5 sm:px-0 mb-3 font-title text-headingm text-neutral-grey-900">
                        {dictionary?.relatedOffers}
                      </h2>
                      <div className="mb:0 py-2 px-5 sm:p-2 flex gap-4 overflow-x-auto scroll-smooth scrollbar-mb [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden mb-6 sm:grid sm:grid-cols-2 sm:gap-5 !last:mr-6">
                        {relatedOffers?.map((offer) => {
                          const logo =
                            offer?.tenant && offer?.tenant.logo
                              ? offer.tenant.logo
                              : offer?.tenant?.brand && offer.tenant.brand?.logo
                                ? offer.tenant.brand.logo
                                : undefined;
                          const tenantName =
                            offer?.tenant && offer.tenant.name
                              ? offer.tenant.name
                              : offer?.tenant && offer.tenant?.brand
                                ? offer.tenant.brand.name
                                : undefined;
                          return (
                            <WithCountdown key={offer?.sys?.id} card={offer}>
                              {({ countdownState }) => (
                                <OfferCard
                                  offer={offer}
                                  header={tenantName}
                                  node_locale={node_locale}
                                  logo={logo}
                                  key={offer?.sys?.id}
                                  parentSlug={parentSlug}
                                  countdownState={countdownState}
                                />
                              )}
                            </WithCountdown>
                          );
                        })}
                      </div>

                      <div>
                        {offerListPageSlug && (
                          <LinkWrapper
                            data={`/${node_locale}/${offerListPageSlug}`}
                            className="flex ml-auto mr-5 sm:mr-0 w-fit text-brand-grey-4 focus-within:outline !no-underline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light rounded-full bg-neutral-grey-100 lg:hover:bg-neutral-grey-200 active:bg-neutral-grey-300 px-6 py-3.5 text-buttonlabell"
                          >
                            {dictionary?.seeAllOffers}
                          </LinkWrapper>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </>
  );
};

export default MeetingPlacePageOffer;

